import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';

import LoginPage from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔄 Auth state changed:', currentUser ? 'Usuario encontrado' : 'No hay usuario');
      
      if (currentUser) {
        console.log('👤 Usuario actual:', {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName
        });
        
        setUser(currentUser);
        
        // Buscar el rol del usuario en Firestore
        console.log('🔍 Buscando rol del usuario en Firestore...');
        try {
          const docRef = doc(db, "users", currentUser.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const userData = docSnap.data();
            console.log('📄 Documento del usuario encontrado:', userData);
            
            if (userData.role) {
              console.log('✅ Rol encontrado:', userData.role);
              setUserRole(userData.role);
            } else {
              console.log('⚠️ No se encontró rol en el documento, asignando "user"');
              setUserRole('user');
            }
          } else {
            console.log('❌ No se encontró documento del usuario en Firestore');
            console.log('🔧 Creando documento de usuario por defecto...');
            
            // Opcionalmente, crear el documento del usuario
            // await setDoc(doc(db, "users", currentUser.uid), {
            //   email: currentUser.email,
            //   displayName: currentUser.displayName,
            //   role: 'user',
            //   createdAt: serverTimestamp()
            // });
            
            setUserRole('user');
          }
        } catch (error) {
          console.error('❌ Error accediendo a Firestore:', error);
          setUserRole('user'); // Valor por defecto en caso de error
        }
      } else {
        console.log('🚪 No hay usuario, limpiando estado...');
        setUser(null);
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Agregar logs adicionales para el estado
  useEffect(() => {
    console.log('📊 Estado actual:', {
      user: user ? `${user.email} (${user.uid})` : 'null',
      userRole,
      loading
    });
  }, [user, userRole, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-main">Verificando permisos...</p>
          <p className="text-sm text-text-light mt-2">
            Usuario: {user ? user.email : 'Ninguno'} | Rol: {userRole || 'Cargando...'}
          </p>
        </div>
      </div>
    );
  }

  // Lógica de Renderizado Basada en Rol
  const renderContent = () => {
    console.log('🎯 Determinando qué renderizar...', { user: !!user, userRole });
    
    if (!user) {
      console.log('➡️ Redirigiendo a login (no hay usuario)');
      return <Navigate to="/login" />;
    }
    
    if (userRole === 'admin') {
      console.log('➡️ Mostrando Dashboard (usuario admin)');
      return <DashboardPage />;
    } else {
      console.log('➡️ Mostrando Landing (usuario normal o sin rol)');
      return <LandingPage />;
    }
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={!user ? <LoginPage /> : <Navigate to="/" />}
        />
        <Route
          path="/*"
          element={renderContent()}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;